import {
  buildReviewableCandidateShell,
  buildReviewableOrigin,
  REVIEWABLE_ORIGIN_COLLECTIONS,
  REVIEWABLE_ORIGIN_TYPES,
} from '../features/reviewableItems/reviewableItemFlow';
import { ALLERGY_REVIEW_DOMAIN, buildAllergyReviewCandidate } from './allergyReviewCandidate';
import { GOAL_REVIEW_DOMAIN, buildGoalReviewCandidate } from './goalReviewCandidate';
import { buildTimelineFactReviewCandidate, TIMELINE_FACT_REVIEW_DOMAIN } from './timelineFactReviewCandidate';
import { getCareKnowledgePromotionDecision } from '../features/careSourceProcessing/careKnowledgeDurabilityPolicy';

const normalizeText = (value = '') => String(value || '').trim();

export const DOCUMENT_REVIEW_DOMAIN = 'document';
export const DOCUMENT_REVIEW_ALLOWED_ACTIONS = ['add_to_timeline', 'add_to_knowledge', 'no_action_needed', 'ignore'];

export const DOCUMENT_EXTRACTED_TYPES = Object.freeze({
  TIMELINE_FACT: 'timeline_fact',
  KNOWLEDGE_FACT: 'knowledge_fact',
  GOAL_FACT: 'goal_fact',
  EMBEDDED_ONLY: 'embedded_only',
});

export const DOCUMENT_TRUST_TIERS = Object.freeze({
  FORMAL: 'formal',
  SOFT: 'soft',
  SELF_AUTHORED: 'self_authored',
});

const buildDocumentCandidateShell = ({
  origin,
  label,
  sourceText,
  sourceAnchor,
  suggestedAction,
  needsReview = false,
  payload,
}) => ({
  ...buildReviewableCandidateShell({
    origin,
    domain: DOCUMENT_REVIEW_DOMAIN,
    label,
    sourceText,
    sourceAnchor,
    allowedActions: DOCUMENT_REVIEW_ALLOWED_ACTIONS,
    suggestedAction,
    confidence: 'high',
    needsReview,
    payload,
  }),
});

const buildOriginInput = (origin) => ({
  id: origin.id,
  type: origin.type,
  collection: origin.collection,
  childId: origin.childId,
  metadata: origin.metadata,
});

const resolveDocumentPromotionDecision = (finding = {}, candidate = {}) => (
  finding.promotionDecision || getCareKnowledgePromotionDecision({
    title: candidate.label || finding.title || finding.label || finding.referringTo,
    content: candidate.sourceText || finding.body || finding.description,
    sourceText: candidate.sourceText || finding.sourceText || finding.body || finding.description,
    knowledgeType: candidate.domain === ALLERGY_REVIEW_DOMAIN
      ? 'allergy'
      : (candidate.domain === GOAL_REVIEW_DOMAIN ? 'goal' : ''),
    sourceType: 'file_import',
    sourceContext: 'imported_document',
    sourceProvenance: { origin: 'imported_document' },
  })
);

const withDocumentPromotionDecision = (candidate, finding) => ({
  ...candidate,
  promotionDecision: resolveDocumentPromotionDecision(finding, candidate),
});

/**
 * Builds document-domain candidates for a saved formal-record Document from
 * the AI extraction step's normalized findings (functions/documentExtraction).
 * The AI reports what it sees in the document's own words, unrestricted by a
 * fixed category list; this resolver is the deterministic step that decides
 * where each finding routes. Only categories covered by the shared Care
 * Knowledge review policy are returned to Add/Skip; other findings remain
 * attached to the source document, and dated facts can be auto-promoted.
 */
const buildAllDocumentDomainCandidatesFromFindings = ({
  originId,
  childId = '',
  findings = {},
} = {}) => {
  const sourceText = [
    ...(findings.dates || []).map((date) => date.sourceText),
    ...(findings.sections || []).map((section) => section.body),
  ].filter(Boolean).join('\n');
  const origin = buildReviewableOrigin({
    type: REVIEWABLE_ORIGIN_TYPES.CARE_SOURCE,
    id: originId,
    collection: REVIEWABLE_ORIGIN_COLLECTIONS.CARE_SOURCES,
    childId,
    sourceText,
  });
  const candidates = [];

  (findings.dates || []).forEach((date) => {
    const label = normalizeText(date.referringTo)
      ? `${date.referringTo}: ${date.dateText}`
      : date.dateText;
    // A timeline_fact candidate carries the real 'timeline_fact' domain — not
    // 'document' — so it dispatches to the real Timeline write via
    // timelineFactCandidateCompletion.js, not a document-domain no-op.
    const timelineCandidate = buildTimelineFactReviewCandidate({
      origin: { ...buildOriginInput(origin), sourceText: date.sourceText || label },
      extractedItem: {
        date: date.dateText,
        label,
        sourceText: date.sourceText || label,
      },
      confidence: 'high',
      suggestedAction: 'add_to_timeline',
    });
    candidates.push(withDocumentPromotionDecision({
      ...timelineCandidate,
      domain: TIMELINE_FACT_REVIEW_DOMAIN,
      payload: {
        ...timelineCandidate.payload,
        sectionTitle: normalizeText(date.referringTo) || date.dateText,
        sectionOrder: candidates.length,
        extractedType: DOCUMENT_EXTRACTED_TYPES.TIMELINE_FACT,
      },
    }, date));
  });

  (findings.allergyInfo || []).forEach((allergy) => {
    // Resolved per Option B: a knowledge_fact candidate carries the REAL domain
    // of the fact it represents (here 'allergy'), not 'document', so it
    // dispatches through the existing allergyCandidateAdapter/
    // completeAllergyCandidateDecision unchanged — no parallel write path.
    const allergyCandidate = buildAllergyReviewCandidate({
      origin: { ...buildOriginInput(origin), sourceText: allergy.sourceText || allergy.description },
      extractedItem: { sourceText: allergy.sourceText || allergy.description },
      confidence: 'high',
      suggestedAction: 'add_allergy',
    });
    candidates.push(withDocumentPromotionDecision({
      ...allergyCandidate,
      domain: ALLERGY_REVIEW_DOMAIN,
      payload: {
        ...allergyCandidate.payload,
        sectionTitle: 'Allergies',
        sectionOrder: candidates.length,
        extractedType: DOCUMENT_EXTRACTED_TYPES.KNOWLEDGE_FACT,
        trustTier: DOCUMENT_TRUST_TIERS.FORMAL,
      },
    }, allergy));
  });

  (findings.goalInfo || []).forEach((goal) => {
    // New lane, same shape as allergy: real 'goal' domain, reuses the existing
    // goalCandidateAdapter/completeGoalCandidateTrackableTargetDecision path
    // into supportProfile.careGoals — no new completion handler.
    const goalCandidate = buildGoalReviewCandidate({
      origin: { ...buildOriginInput(origin), sourceText: goal.sourceText || goal.description },
      extractedItem: {
        title: goal.description,
        sourceText: goal.sourceText || goal.description,
      },
      confidence: 'high',
      suggestedAction: 'add_goal',
    });
    candidates.push(withDocumentPromotionDecision({
      ...goalCandidate,
      domain: GOAL_REVIEW_DOMAIN,
      payload: {
        ...goalCandidate.payload,
        sectionTitle: 'Goals',
        sectionOrder: candidates.length,
        extractedType: DOCUMENT_EXTRACTED_TYPES.GOAL_FACT,
      },
    }, goal));
  });

  // Bug fix: the AI can (and did, against the real document) return an
  // "Allergies" section in `sections[]` in addition to populating
  // `allergyInfo[]` with the same content — that produced a duplicate
  // allergy candidate (one via the real allergy domain, one as embedded_only).
  // Once allergyInfo has already produced a candidate, skip any section whose
  // title is an allergy heading rather than re-emitting the same content.
  const allergySectionTitlePattern = /^allerg(y|ies)$/i;
  const hasAllergyCandidate = (findings.allergyInfo || []).length > 0;

  (findings.sections || []).forEach((section) => {
    if (hasAllergyCandidate && allergySectionTitlePattern.test(normalizeText(section.title))) {
      return;
    }
    candidates.push(withDocumentPromotionDecision(buildDocumentCandidateShell({
      origin,
      label: section.title,
      sourceText: section.body,
      sourceAnchor: section.title,
      suggestedAction: 'no_action_needed',
      needsReview: !section.body,
      payload: {
        sectionTitle: section.title,
        sectionOrder: section.sectionOrder ?? candidates.length,
        extractedType: DOCUMENT_EXTRACTED_TYPES.EMBEDDED_ONLY,
      },
    }), section));
  });

  // Anything the AI noticed but couldn't map to dates/sections/allergy/goal
  // remains embedded in the saved document. The promotion policy decides
  // whether it also needs an explicit caregiver decision.
  (findings.other || []).forEach((item) => {
    candidates.push(withDocumentPromotionDecision(buildDocumentCandidateShell({
      origin,
      label: item.label,
      sourceText: item.sourceText || item.description,
      sourceAnchor: item.label,
      suggestedAction: 'no_action_needed',
      needsReview: true,
      payload: {
        sectionTitle: item.label,
        sectionOrder: candidates.length,
        extractedType: DOCUMENT_EXTRACTED_TYPES.EMBEDDED_ONLY,
        description: item.description,
      },
    }), item));
  });

  return candidates;
};

export const buildDocumentDomainCandidatesFromFindings = (options = {}) => (
  buildAllDocumentDomainCandidatesFromFindings(options)
    .filter((candidate) => candidate.promotionDecision?.needsCaregiverDecision)
);

export const buildAutoPromotableDocumentTimelineCandidatesFromFindings = (options = {}) => {
  return buildAllDocumentDomainCandidatesFromFindings(options).filter((candidate) => (
    candidate.domain === TIMELINE_FACT_REVIEW_DOMAIN
    && candidate.promotionDecision?.action === 'auto_promote'
  ));
};

/**
 * Frontend half of wiring functions/documentExtraction into the live
 * pipeline: captureArtifactRouter.js (backend) attaches `documentFindings`
 * to its routing result when the "document" bucket is present
 * (routeCaptureArtifactsWithDocumentFindings.js). This reads that attached
 * result and builds candidates from it — the two halves can't share one
 * function call directly since the router is backend CommonJS and this
 * candidate builder is frontend ES modules with frontend-only dependencies.
 */
export const buildDocumentDomainCandidatesFromRoutingResult = ({
  originId,
  childId = '',
  routingResult = {},
} = {}) => {
  const findings = routingResult?.documentFindings;
  if (!findings || findings.ok === false) {
    return [];
  }
  return buildDocumentDomainCandidatesFromFindings({ originId, childId, findings });
};

export default buildDocumentDomainCandidatesFromFindings;
